import type {Middleware} from '@linx-security/api-specs';
import type {Logger} from 'pino';

import {HTTPStatus} from '../types';

import {baseLogger} from './log-middleware';

export const IP_NOT_ALLOWED_TYPE = `ip_not_allowed`;

const isIpNotAllowedBody = (body: unknown): boolean => {
  if (!body || typeof body !== `object`) return false;
  const topLevelType = (body as {type?: unknown}).type;
  const wrappedType = (body as {error?: {type?: unknown}}).error?.type;
  return topLevelType === IP_NOT_ALLOWED_TYPE || wrappedType === IP_NOT_ALLOWED_TYPE;
};

/**
 * Detects 403 responses from the tenant IP allowlist gate and triggers
 * `onIpNotAllowed` so callers can redirect the user to the static
 * "IP not allowed" page. Backend returns the body either as
 * `{ type: "ip_not_allowed", ... }` (top-level) or wrapped under `error`.
 */
export const getIpNotAllowedMiddleware = (
  base: Logger = baseLogger,
  onIpNotAllowed: (response: Response) => void | Promise<void>,
) => {
  const logger = base.child({scope: `ip-not-allowed-middleware`}, {});

  return {
    post: async context => {
      if (!context.response) return context.response;
      if (context.response.status !== HTTPStatus.FORBIDDEN) return context.response;

      let body: unknown;
      try {
        body = await context.response.clone().json();
      } catch {
        return context.response;
      }

      if (!isIpNotAllowedBody(body)) return context.response;

      logger.info({url: context.url}, `Tenant IP allowlist blocked request, redirecting`);
      await onIpNotAllowed(context.response);

      // Block the middleware chain so subsequent middlewares (notably
      // cast-error, which would throw an ApiError that SWR renders as an
      // error boundary) never run before the browser-side navigation
      // completes. The RSC redirect() callback throws NEXT_REDIRECT before
      // reaching this line, so this only matters for the client path.
      return new Promise<Response>(() => {});
    },
  } satisfies Middleware;
};
